import type { QueryClient, UseQueryOptions } from '@tanstack/react-query'
import { FOR_YOU_PREVIEW_LIMIT, type SidepanelChatApiItem } from './running-chat-selection'
import { FOR_YOU_CHATS_QUERY_KEY } from '@renderer/stores/conversation-query-keys'
import type { ForYouChatsScope } from './running-chat-query-keys'

const MEETING_CHATS_LIMIT = 100

interface ForYouChatsQueryOptions {
  queryClient: QueryClient
  fetchConversations: (query: string) => Promise<Array<SidepanelChatApiItem>>
  scope?: ForYouChatsScope
  enabled: boolean
}

export function forYouChatsQueryOptions({
  queryClient,
  fetchConversations,
  scope,
  enabled,
}: ForYouChatsQueryOptions): UseQueryOptions<Array<SidepanelChatApiItem>> {
  const meetingId = scope?.audioNoteId ?? scope?.audioSessionId ?? undefined
  const meetingParam = scope?.audioNoteId ? 'audioNoteId' : 'audioSessionId'
  const query = new URLSearchParams({
    surface: 'for-you',
    includeChatActions: 'true',
    limit: String(scope ? MEETING_CHATS_LIMIT : FOR_YOU_PREVIEW_LIMIT),
  })
  if (meetingId) {
    query.set(meetingParam, meetingId)
  }

  return {
    queryKey: scope ? [FOR_YOU_CHATS_QUERY_KEY, meetingParam, meetingId] : [FOR_YOU_CHATS_QUERY_KEY],
    queryFn: () => fetchConversations(query.toString()),
    enabled: enabled && (!scope || !!meetingId),
    staleTime: 60 * 1000,
    placeholderData: () => {
      if (!enabled || !scope?.audioNoteId || !scope.audioSessionId) {
        return undefined
      }
      // Placeholder data preserves live rows without marking the saved-note query fresh.
      const liveChats = queryClient.getQueryData<Array<SidepanelChatApiItem>>([
        FOR_YOU_CHATS_QUERY_KEY,
        'audioSessionId',
        scope.audioSessionId,
      ])
      return liveChats?.length ? liveChats : undefined
    },
  }
}
